import type { ReactNode } from 'react'
import type { AppId, WindowState } from './types'

import { Bezel } from './bezel'
import { StartMenu } from './start-menu'
import { raisedShadow, sunkenShadow, useDesk95Theme } from './theme'

export const TASKBAR_H = 28

type TaskbarProps = {
    windows: WindowState[]
    activeUid: string | null
    compact: boolean
    clock: string
    startOpen: boolean
    onToggleStart: () => void
    onLaunch: (app: AppId) => void
    onSelectWindow: (uid: string) => void
}

const StartFlag = (): ReactNode => (
    <svg viewBox='0 0 16 16' width='16' height='16' shapeRendering='crispEdges' aria-hidden='true'>
        <path fill='#ff3b30' d='M1 3h6v5H1z' />
        <path fill='#4cd964' d='M8 2h7v6H8z' />
        <path fill='#0a84ff' d='M1 9h6v5H1z' />
        <path fill='#ffcc00' d='M8 9h7v6H8z' />
    </svg>
)

export const Taskbar = ({
    windows,
    activeUid,
    compact,
    clock,
    startOpen,
    onToggleStart,
    onLaunch,
    onSelectWindow
}: TaskbarProps): ReactNode => {
    const palette = useDesk95Theme()

    return (
        <Bezel
            className='absolute inset-x-0 bottom-0 flex items-center gap-[4px] px-[2px]'
            thin
            style={{ height: TASKBAR_H, zIndex: 8000 }}
        >
            <div className='relative shrink-0'>
                {startOpen && <StartMenu onLaunch={onLaunch} />}

                <button
                    type='button'
                    className='flex h-[22px] items-center gap-[4px] px-[5px] text-[12px] leading-none font-bold'
                    style={{
                        boxShadow: startOpen ? sunkenShadow(palette, true) : raisedShadow(palette),
                        background: palette.face,
                        color: palette.text,
                        cursor: 'default'
                    }}
                    onPointerDown={event => event.stopPropagation()}
                    onClick={onToggleStart}
                    aria-expanded={startOpen}
                    aria-haspopup='menu'
                >
                    <StartFlag />
                    Start
                </button>
            </div>

            <div
                className='h-[20px] w-[3px] shrink-0'
                style={{ borderLeft: `1px solid ${palette.shadow}`, borderRight: `1px solid ${palette.highlight}` }}
                aria-hidden='true'
            />

            <div className='flex min-w-0 flex-1 items-center gap-[3px]'>
                {windows.map(win => {
                    const pressed = win.uid === activeUid && !win.minimized

                    return (
                        <button
                            key={win.uid}
                            type='button'
                            className='flex h-[22px] min-w-0 items-center gap-[4px] px-[4px] text-[11px] leading-none'
                            style={{
                                boxShadow: pressed ? sunkenShadow(palette, true) : raisedShadow(palette),
                                background: palette.face,
                                color: palette.text,
                                maxWidth: compact ? 30 : 160,
                                flex: compact ? '0 0 auto' : '1 1 0%',
                                cursor: 'default'
                            }}
                            onClick={() => onSelectWindow(win.uid)}
                            title={win.title}
                        >
                            <img src={win.icon} alt='' width={14} height={14} className='desk95-pixel size-[14px] shrink-0' />

                            {!compact && (
                                <span className='truncate'>
                                    {win.title}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>

            <Bezel
                variant='sunken'
                thin
                className='flex h-[20px] shrink-0 items-center px-[7px] text-[11px] leading-none'
                face={false}
                style={{ color: palette.text, minWidth: 62, justifyContent: 'center' }}
            >
                {clock}
            </Bezel>
        </Bezel>
    )
}
